import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * Selection of the active workspace (tenant) and Discord guild.
 *
 * The selection is persisted to localStorage so it survives navigation and page
 * reloads — otherwise every route (billing, checkout, usage) would reset to
 * "select a workspace" on refresh and a customer could never actually reach the
 * buy flow. Only non-secret identifiers/labels are stored (never tokens, cards,
 * or credentials), matching ODIN's "no client-side secrets" posture. A page
 * still shows an honest "select a workspace/server" prompt when the selection is
 * missing rather than fabricating context.
 */
export interface WorkspaceSelection {
  tenantId: string | null;
  tenantName: string | null;
  /** Internal ODIN guild id (not the raw Discord snowflake). */
  guildId: string | null;
  guildName: string | null;
}

interface AppContextValue extends WorkspaceSelection {
  setWorkspace: (tenantId: string, tenantName: string) => void;
  clearWorkspace: () => void;
  setGuild: (guildId: string, guildName: string) => void;
  clearGuild: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

const STORAGE_KEY = "odin.workspace-selection.v1";

const EMPTY_SELECTION: WorkspaceSelection = {
  tenantId: null,
  tenantName: null,
  guildId: null,
  guildName: null,
};

/** Restore a previously chosen workspace/guild, tolerating missing or malformed storage. */
function loadSelection(): WorkspaceSelection {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_SELECTION;
    const parsed = JSON.parse(raw) as Partial<WorkspaceSelection>;
    return {
      tenantId: typeof parsed.tenantId === "string" ? parsed.tenantId : null,
      tenantName: typeof parsed.tenantName === "string" ? parsed.tenantName : null,
      guildId: typeof parsed.guildId === "string" ? parsed.guildId : null,
      guildName: typeof parsed.guildName === "string" ? parsed.guildName : null,
    };
  } catch {
    return EMPTY_SELECTION;
  }
}

export function AppContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const [sel, setSel] = useState<WorkspaceSelection>(loadSelection);

  // Persist selection so a refresh keeps the customer on their workspace.
  useEffect(() => {
    try {
      if (sel.tenantId) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sel));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Storage unavailable (private mode, etc.) — selection stays in-memory only.
    }
  }, [sel]);

  const value = useMemo<AppContextValue>(
    () => ({
      ...sel,
      setWorkspace: (tenantId, tenantName) =>
        setSel((s) =>
          s.tenantId === tenantId ? s : { tenantId, tenantName, guildId: null, guildName: null },
        ),
      clearWorkspace: () => setSel(() => EMPTY_SELECTION),
      setGuild: (guildId, guildName) => setSel((s) => ({ ...s, guildId, guildName })),
      clearGuild: () => setSel((s) => ({ ...s, guildId: null, guildName: null })),
    }),
    [sel],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext must be used within an AppContextProvider");
  return ctx;
}
